import { useCallback, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDocFromServer } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { cacheConsent } from "@/lib/mockAuth";
import { syncSubscriptionFromFirestore } from "@/lib/subscription";

export function useAccountSession() {
  const [state, setState] = useState({ status: "loading", user: null });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  useEffect(() => {
    let revision = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const run = ++revision;
      const current = () => run === revision && auth.currentUser === user;
      if (!user) { setState({ status: "guest", user: null }); return; }
      if (!user.emailVerified) { setState({ status: "unverified", user }); return; }
      setState({ status: "loading", user });
      try {
        const snapshot = await getDocFromServer(doc(db, "users", user.uid));
        if (!current()) return;
        const consent = snapshot.exists() ? snapshot.data()?.consent : null;
        cacheConsent(user.uid, consent);
        if (!consent?.data) { setState({ status: "consent", user }); return; }
        await syncSubscriptionFromFirestore(user.uid);
        if (current()) setState({ status: "ready", user });
      } catch {
        if (current()) setState({ status: "error", user });
      }
    });
    return () => { revision += 1; unsubscribe(); };
  }, [attempt]);
  if (state.user && auth.currentUser !== state.user) return { status: "loading", retry };
  return { ...state, retry };
}
